import { useQuery } from "@apollo/client";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { GET_EMBEDDED_DOCUMENT_PREPARATION_URL } from "queries/submissions";
import { useEffect, useRef, useState } from "react";
import { BOLDSIGN_ORIGIN, BoldSignData } from "./boldSignClientSideEvents";

interface EmbeddedDocumentPreparationUrlDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
}

export function EmbeddedDocumentPreparationUrlDialog({
  open,
  onClose,
  submissionId,
}: EmbeddedDocumentPreparationUrlDialogProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const onMessage = (e: MessageEvent<BoldSignData>) => {
      if (e.origin !== BOLDSIGN_ORIGIN) return;
      if (!e.data) return;

      if (e.data === "onCreateSuccess") {
        setIsSuccess(true);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const { data, loading } = useQuery(GET_EMBEDDED_DOCUMENT_PREPARATION_URL, {
    variables: { submissionId },
    fetchPolicy: "network-only",
  });

  const preparationUrl = data?.getEmbeddedDocumentPreparationUrl;

  if (!loading && !preparationUrl) {
    return null;
  }

  if (isSuccess) {
    return (
      <Dialog
        title="Document Preparation"
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        noPrimaryAction
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          height="100%"
          pt={4}
          pb={8}
        >
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32 }} />
          <Typography>Document successfully prepared</Typography>
        </Stack>
      </Dialog>
    );
  }

  return (
    <Dialog
      title="Document Preparation"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
      contentSx={{ height: "100%" }}
      DialogContentProps={{ sx: { p: 0 } }}
      noPrimaryAction
    >
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      )}
      {preparationUrl && !isSuccess && (
        <iframe
          src={preparationUrl}
          ref={iframeRef}
          width="100%"
          height="100%"
          frameBorder="0"
        />
      )}
    </Dialog>
  );
}
